import type { LucideIcon } from 'lucide-react'

import {
    Activity,
    BarChart3,
    HeartPulse,
    MapPin,
    Target,
    Trophy
} from 'lucide-react'

export const marathonBrand = {
    name: 'Marathon',
    tagline: 'Run with intention.',
    description:
        'Marathon is the running companion that turns every mile into momentum, with live tracking, cardio zones, and adaptive coaching built for runners who want more.'
}

export const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'Training', href: '#showcase' },
    { label: 'Community', href: '#community' },
    { label: 'Pricing', href: '#pricing' }
]

export interface Feature {
    icon: LucideIcon
    title: string
    body: string
    accent: string
}

export const features: Feature[] = [
    {
        icon: MapPin,
        title: 'Live GPS Tracking',
        body: 'Map every route with precise pace, splits, and elevation, updating in real time as you move.',
        accent: '#E94E1B'
    },
    {
        icon: HeartPulse,
        title: 'Heart Rate Zones',
        body: 'Train in the right zone with live cardio telemetry and recovery insights after every session.',
        accent: '#C42E5A'
    },
    {
        icon: Target,
        title: 'Adaptive Coaching',
        body: 'Plans that reshape themselves around your fitness, your schedule, and the race on your calendar.',
        accent: '#E94E1B'
    },
    {
        icon: BarChart3,
        title: 'Weekly Insights',
        body: 'Streaks, records, and trends laid out clearly so you always know whether you are gaining ground.',
        accent: '#168F4E'
    },
    {
        icon: Activity,
        title: 'Recovery Score',
        body: 'A daily readiness score tells you when to push for a personal best and when to hold back.',
        accent: '#C42E5A'
    },
    {
        icon: Trophy,
        title: 'Community Challenges',
        body: 'Join clubs, climb the leaderboard, and earn medals that mean something to people who run.',
        accent: '#168F4E'
    }
]

export interface ShowcaseItem {
    eyebrow: string
    title: string
    body: string
    bullets: string[]
    image: string
    accent: string
}

export const showcaseItems: ShowcaseItem[] = [
    {
        eyebrow: 'On the run',
        title: 'Your route, breathing through the screen',
        body: 'Marathon paints your path as you run it, with live pace, distance, and elevation that react to every turn of the trail.',
        bullets: ['Real-time pace and splits', 'Elevation and segment tracking', 'Auto-pause on rest stops'],
        image: '/images/run-trail.jpg',
        accent: '#E94E1B'
    },
    {
        eyebrow: 'Heart & breath',
        title: 'Cardio telemetry, rendered as the horizon',
        body: 'See your heart rate zones move in real time, with breathing rhythm and HRV that help you train at exactly the right intensity.',
        bullets: ['Five-zone heart rate model', 'Live ECG-style waveform', 'HRV and recovery readiness'],
        image: '/images/run-shoes.jpg',
        accent: '#C42E5A'
    },
    {
        eyebrow: 'This week',
        title: 'Every mile, streak, and record in one place',
        body: 'A weekly view that celebrates the work, from daily distance to personal records and the medals you earned along the way.',
        bullets: ['Daily distance breakdown', 'Personal records and streaks', 'Shareable weekly recaps'],
        image: '/images/run-community.jpg',
        accent: '#168F4E'
    }
]

export const stats = [
    { value: '2.4M', label: 'Runners moving' },
    { value: '612M', label: 'Kilometres logged' },
    { value: '18M', label: 'Personal records set' },
    { value: '4.9', label: 'Average app rating' }
]

export interface Testimonial {
    quote: string
    name: string
    role: string
    initials: string
    accent: string
}

export const testimonials: Testimonial[] = [
    {
        quote: 'Marathon turned my scattered jogs into a real training block. I shaved four minutes off my 10K in a single season.',
        name: 'Lena Okafor',
        role: 'Boston qualifier',
        initials: 'LO',
        accent: '#E94E1B'
    },
    {
        quote: 'The recovery score is uncanny. It told me to back off the day before I would have overtrained, every single time.',
        name: 'Marcus Reyes',
        role: 'Trail ultrarunner',
        initials: 'MR',
        accent: '#C42E5A'
    },
    {
        quote: 'Our running club lives inside the challenges now. Leaderboards on Monday morning are the highlight of the week.',
        name: 'Sofia Lindqvist',
        role: 'Stockholm Run Club',
        initials: 'SL',
        accent: '#168F4E'
    }
]

export interface PricingTier {
    name: string
    price: string
    period: string
    blurb: string
    features: string[]
    cta: string
    featured: boolean
}

export const pricingTiers: PricingTier[] = [
    {
        name: 'Base',
        price: '$0',
        period: 'forever',
        blurb: 'Everything you need to start logging runs and building a habit.',
        features: ['Live GPS tracking', 'Basic pace and distance', 'Weekly summary', 'Up to 3 routes saved'],
        cta: 'Start free',
        featured: false
    },
    {
        name: 'Pro',
        price: '$9',
        period: 'per month',
        blurb: 'For runners chasing a goal who want coaching and deeper insight.',
        features: ['Everything in Base', 'Heart rate zones and HRV', 'Adaptive training plans', 'Recovery score', 'Unlimited routes'],
        cta: 'Go Pro',
        featured: true
    },
    {
        name: 'Elite',
        price: '$19',
        period: 'per month',
        blurb: 'Race-grade analytics and a personal edge for the competitive runner.',
        features: ['Everything in Pro', 'Race-day strategy planner', 'Advanced cardio analytics', 'Priority club challenges', '1:1 coach check-ins'],
        cta: 'Go Elite',
        featured: false
    }
]

export const faqs = [
    {
        q: 'Does Marathon work without a smartwatch?',
        a: 'Yes. Marathon tracks pace, distance, and route straight from your phone. Pair a watch or chest strap when you want heart rate and recovery data.'
    },
    {
        q: 'Can I import my history from another app?',
        a: 'You can bring your past runs across in a few taps. Marathon rebuilds your streaks, records, and totals so nothing is lost.'
    },
    {
        q: 'Will the adaptive plan fit a race date?',
        a: 'Set your goal race and target time, and Marathon builds a plan that ramps and tapers to land you on the start line ready.'
    },
    {
        q: 'Is my location data private?',
        a: 'Your routes are yours. You choose what to share, and you can hide start and end zones from any run you post.'
    }
]

export const footerColumns = [
    {
        title: 'Product',
        links: ['Features', 'Training plans', 'Community', 'Pricing', 'Download']
    },
    {
        title: 'Company',
        links: ['About', 'Careers', 'Press', 'Blog']
    },
    {
        title: 'Resources',
        links: ['Help center', 'Runner guides', 'Status', 'Contact']
    },
    {
        title: 'Legal',
        links: ['Privacy', 'Terms', 'Cookies']
    }
]
